import type { AutomationNode } from '../nodes/types';

export const SNAP_THRESHOLD = 5; // pixels

export type HelperLineResult = {
  horizontal: number | null; // Y position for horizontal line
  vertical: number | null; // X position for vertical line
  snapPosition: { x: number; y: number };
};

type NodeBounds = {
  left: number;
  right: number;
  top: number;
  bottom: number;
  centerX: number;
  centerY: number;
  width: number;
  height: number;
};

export function getNodeBounds(node: AutomationNode): NodeBounds {
  const width = node.measured?.width ?? 220;
  const height = node.measured?.height ?? 80;

  return {
    left: node.position.x,
    right: node.position.x + width,
    top: node.position.y,
    bottom: node.position.y + height,
    centerX: node.position.x + width / 2,
    centerY: node.position.y + height / 2,
    width,
    height,
  };
}

type AlignmentCheck = {
  type: 'vertical' | 'horizontal';
  position: number;
  snapOffset: number;
  priority: number; // Lower is higher priority
};

export function getHelperLines(
  draggingNode: AutomationNode,
  otherNodes: AutomationNode[],
  threshold: number = SNAP_THRESHOLD,
): HelperLineResult {
  const draggingBounds = getNodeBounds(draggingNode);
  let bestVertical: AlignmentCheck | null = null;
  let bestHorizontal: AlignmentCheck | null = null;

  // Check alignments with all other nodes
  for (const otherNode of otherNodes) {
    if (otherNode.id === draggingNode.id) continue;

    const otherBounds = getNodeBounds(otherNode);

    // Vertical alignments (check X positions for vertical lines)
    const verticalChecks: AlignmentCheck[] = [
      // Center to center (highest priority)
      {
        type: 'vertical',
        position: otherBounds.centerX,
        snapOffset: otherBounds.centerX - draggingBounds.centerX,
        priority: 0,
      },
      // Left to left
      {
        type: 'vertical',
        position: otherBounds.left,
        snapOffset: otherBounds.left - draggingBounds.left,
        priority: 1,
      },
      // Right to right
      {
        type: 'vertical',
        position: otherBounds.right,
        snapOffset: otherBounds.right - draggingBounds.right,
        priority: 1,
      },
      // Left to right
      {
        type: 'vertical',
        position: otherBounds.right,
        snapOffset: otherBounds.right - draggingBounds.left,
        priority: 2,
      },
      // Right to left
      {
        type: 'vertical',
        position: otherBounds.left,
        snapOffset: otherBounds.left - draggingBounds.right,
        priority: 2,
      },
    ];

    // Horizontal alignments (check Y positions for horizontal lines)
    const horizontalChecks: AlignmentCheck[] = [
      // Center to center (highest priority)
      {
        type: 'horizontal',
        position: otherBounds.centerY,
        snapOffset: otherBounds.centerY - draggingBounds.centerY,
        priority: 0,
      },
      // Top to top
      {
        type: 'horizontal',
        position: otherBounds.top,
        snapOffset: otherBounds.top - draggingBounds.top,
        priority: 1,
      },
      // Bottom to bottom
      {
        type: 'horizontal',
        position: otherBounds.bottom,
        snapOffset: otherBounds.bottom - draggingBounds.bottom,
        priority: 1,
      },
      // Top to bottom
      {
        type: 'horizontal',
        position: otherBounds.bottom,
        snapOffset: otherBounds.bottom - draggingBounds.top,
        priority: 2,
      },
      // Bottom to top
      {
        type: 'horizontal',
        position: otherBounds.top,
        snapOffset: otherBounds.top - draggingBounds.bottom,
        priority: 2,
      },
    ];

    // Find best vertical alignment
    for (const check of verticalChecks) {
      const distance = Math.abs(check.snapOffset);
      if (distance <= threshold) {
        if (
          !bestVertical ||
          check.priority < bestVertical.priority ||
          (check.priority === bestVertical.priority &&
            distance < Math.abs(bestVertical.snapOffset))
        ) {
          bestVertical = check;
        }
      }
    }

    // Find best horizontal alignment
    for (const check of horizontalChecks) {
      const distance = Math.abs(check.snapOffset);
      if (distance <= threshold) {
        if (
          !bestHorizontal ||
          check.priority < bestHorizontal.priority ||
          (check.priority === bestHorizontal.priority &&
            distance < Math.abs(bestHorizontal.snapOffset))
        ) {
          bestHorizontal = check;
        }
      }
    }

    // Early exit if we found highest priority alignments
    if (bestVertical?.priority === 0 && bestHorizontal?.priority === 0) {
      break;
    }
  }

  // Calculate snap position
  const snapX = bestVertical
    ? draggingNode.position.x + bestVertical.snapOffset
    : draggingNode.position.x;
  const snapY = bestHorizontal
    ? draggingNode.position.y + bestHorizontal.snapOffset
    : draggingNode.position.y;

  return {
    horizontal: bestHorizontal?.position ?? null,
    vertical: bestVertical?.position ?? null,
    snapPosition: { x: snapX, y: snapY },
  };
}
